import { useState, type ReactNode } from 'react'
import { FrontDoorProvider, RequireAppAccess, rememberNext, safeNext, takeNext, useMe } from '@omni/front-door'
import { Splash } from '../components/Splash'
import { productName } from '../lib/config'
import { surfaces } from '../surfaces'
import { FRONT_DOOR_APP, FRONT_PAGE_URL, frontDoor, useTenantRpc } from './config'
import { AccessError, NoAccess, NotOnList } from './screens'

// The signed-in side of the front door (doc sections 5, 6 and 11), wrapped
// around the shell's chrome on front-door builds only (App.tsx):
//  - reads "who am I" and "my apps" once for the page (FrontDoorProvider);
//  - on the front-door site, follows a checked `next` back to the app the
//    person came from;
//  - not on the list: one screen, no tabs, a sign-out button;
//  - on an app build: the app shows only when the list grants it, re-checked
//    on focus and every 60 seconds (RequireAppAccess).
// If the list cannot be read (not switched on yet, or the database is down),
// the chrome still renders and the front page says so; the database refuses
// rows to anyone not on the list regardless.

const store = (): Storage | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** Called on the front-door build while signed out, before the sign-in screen. */
export function rememberNextForSignIn(): void {
  if (FRONT_DOOR_APP) return
  const s = store()
  if (s) rememberNext(s, window.location.search, frontDoor)
}

/** Once signed in on the front-door site: where to send the person, if anywhere. */
function pendingNext(): string | null {
  if (FRONT_DOOR_APP) return null
  const s = store()
  const stored = s ? takeNext(s, frontDoor) : null
  const fromUrl = safeNext(new URLSearchParams(window.location.search).get('next'), frontDoor)
  const next = stored ?? fromUrl
  return next && next !== window.location.href ? next : null
}

export function FrontDoorGate({ children }: { children: ReactNode }) {
  const rpc = useTenantRpc()
  return (
    <FrontDoorProvider rpc={rpc}>
      <Gate>{children}</Gate>
    </FrontDoorProvider>
  )
}

const APP_LABEL = surfaces[0]?.meta.label ?? productName

function Gate({ children }: { children: ReactNode }) {
  const me = useMe()
  const [next] = useState(pendingNext)
  if (next) {
    window.location.replace(next)
    return <Splash label={`Opening ${productName}…`} />
  }
  if (me.status === 'loading') return <Splash label={`Opening ${productName}…`} />
  if (me.status === 'ready' && me.data === null) return <NotOnList />
  if (!FRONT_DOOR_APP) return <>{children}</>
  return (
    <RequireAppAccess
      app={FRONT_DOOR_APP}
      frontPageUrl={FRONT_PAGE_URL}
      renderLoading={() => <Splash label={`Opening ${APP_LABEL}…`} />}
      renderDenied={(url) => <NoAccess app={APP_LABEL} frontPageUrl={url} />}
      renderError={(retry) => <AccessError app={APP_LABEL} retry={retry} />}
    >
      {children}
    </RequireAppAccess>
  )
}
